/**
 * dogrulama.tsx
 *
 * NE İŞE YARAR : İki aşamalı doğrulama ekranı — `AU-02` ile kodu doğrular, `AU-03` ile
 *                yeniden gönderir, cihazı kaydeder
 * KAYNAK SPEC  : 15-arayuz-standardi.md §15 (2FA ekranı, cihazı hatırla) · §15.1 (kurallar)
 *                04-api-sozlesmesi.md AU-02 · AU-03 · ADR-019 §4 (kod kuralları) · §5 (cihaz)
 * BAĞLI OLDUĞU : src/api/istemci.ts · src/oturum/* · src/sabit/kimlik.ts
 * KULLANAN     : src/uygulama.tsx — `/giris/dogrulama` yolu
 * DİKKAT       : 1) EKRAN DOĞRUDAN AÇILAMAZ. `challenge_id` gezinti durumundan gelir;
 *                   yoksa giriş ekranına dönülür. Yol parametresine konsaydı bir
 *                   challenge kimliği tarayıcı geçmişinde, kayıtlarda ve paylaşılan
 *                   bağlantılarda kalırdı.
 *                2) KALAN SÜRE İSTEMCİDE SAYILIR ÇÜNKÜ SUNUCU DÖNMÜYOR
 *                   (`src/sabit/kimlik.ts` notu 2). `15` §15.1 *"kalan süre görünür"*
 *                   diyor; `04` AU-01'in yanıtında challenge'ın bitiş anı yok. Sayaç
 *                   yanıtın alındığı andan başlar ve **karar vermez**: süre bittiğinde
 *                   ekran yalnız yeni kod istemeyi önerir, doğrulamayı kendisi reddetmez.
 *                   Gerçek kararı `expires_at` üzerinden sunucu verir.
 *                3) DENEME SAYACI DA İSTEMCİDEDİR ÇÜNKÜ SUNUCU AYRIM YAPMIYOR. `04`
 *                   AU-02 yanlış, süresi geçmiş ve kullanılmış kodu **aynı `401`** ile
 *                   döndürür (ADR-008 §8). `15` §15.1 ekranın *"3 yanlış denemeden sonra
 *                   kod geçersiz"* demesini istiyor; ekran bunu ancak kendi denemelerini
 *                   sayarak söyleyebilir. Bu sayaç bir güvenlik mekanizması **değildir**
 *                   — gerçek sınır `mfa_challenge.attempt_count`tır ve sunucudadır.
 *                4) CİHAZI HATIRLA VARSAYILAN OLARAK İŞARETLİDİR (`15` §15). İşaretsiz
 *                   bırakılsaydı her giriş 2FA isterdi ve kullanıcı, kurumun karar
 *                   verdiği 60 günlük kolaylığı hiç görmezdi.
 *                5) CİHAZ ADI KULLANICI TARAFINDAN YAZILIR VE UYARISI ZORUNLUDUR
 *                   (`15` §15.1): *"Kişisel bilgi yazmayınız."* Ad `user_device`ta
 *                   saklanır ve yöneticinin cihaz listesinde görünür; kimlik numarası
 *                   yazan bir kullanıcı onu farkında olmadan paylaşmış olur.
 *                6) CİHAZ SIRRI GELDİĞİ ANDA SAKLANIR VE **BİR DAHA DÖNMEZ** (`04`
 *                   AU-02). Saklanmazsa kaybolur; kullanıcı bir sonraki girişte yeniden
 *                   2FA görür ve sebebi hiçbir yerde görünmez.
 *                7) CİHAZ SINIRI DOLDUĞUNDA OTURUM YİNE DE AÇILIR (`04` AU-02). Girişi
 *                   reddetmek, eski cihazını silmek için giriş yapması gereken kullanıcıyı
 *                   kilitlerdi. Ekran bunu kapanmayan bir bildirimle ve **cihaz listesine
 *                   doğrudan bağlantıyla** söyler (`15` §15.1).
 *                8) YENİDEN GÖNDERME YENİ BİR `challenge_id` ÜRETİR (`04` AU-03) ve
 *                   eskisini kapatır. Kimlik güncellenmeseydi ekran, sunucunun artık
 *                   kabul etmediği bir challenge'a kod göndermeye devam ederdi.
 * SON GÜNCELLEME: 2026-08-31 12:40:00
 */
import {
  Anchor,
  Button,
  Checkbox,
  Group,
  Input,
  PinInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiHatasi } from '../../api/hata';
import { istek } from '../../api/istemci';
import { metin, saniyeyiBicimle } from '../../metin';
import { useOturum } from '../../oturum/baglam';
import { cihazSirriniYaz } from '../../oturum/depo';
import {
  CIHAZ_TURU_WEB,
  KOD_DENEME_HAKKI,
  KOD_GECERLILIK_SANIYE,
  KOD_UZUNLUGU,
  YENIDEN_GONDER_BEKLEME_SANIYE,
} from '../../sabit/kimlik';
import { HataUyarisi } from '../ortak/hata-uyarisi';
import { KimlikDuzeni } from './duzen';
import type { DogrulamaDurumu } from './giris';

/** `04` AU-02 yanıtı. */
interface DogrulamaYaniti {
  readonly session: { readonly token: string; readonly expires_at: string };
  readonly device_secret?: string;
  readonly device_limit_reached?: boolean;
}

/** `04` AU-03 yanıtı — **yeni** challenge kimliği (başlıktaki not 8). */
interface YenidenGonderYaniti {
  readonly challenge_id: string;
  readonly phone_masked?: string;
}

export function DogrulamaEkrani(): ReactElement {
  const konum = useLocation();
  const gelen = konum.state as DogrulamaDurumu | null;

  // Başlıktaki not 1 — durum yoksa ekran anlamsızdır.
  if (gelen === null || gelen.challengeId === '') {
    return <Navigate to="/giris" replace />;
  }

  return <DogrulamaFormu baslangic={gelen} />;
}

function DogrulamaFormu({ baslangic }: { baslangic: DogrulamaDurumu }): ReactElement {
  const { oturumuBaslat } = useOturum();
  const yonlendir = useNavigate();

  const [challengeId, setChallengeId] = useState(baslangic.challengeId);
  const [telefonMaskeli, setTelefonMaskeli] = useState(baslangic.telefonMaskeli);
  const [kod, setKod] = useState('');
  const [cihaziHatirla, setCihaziHatirla] = useState(true); // Başlıktaki not 4.
  const [cihazAdi, setCihazAdi] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hataMesaji, setHataMesaji] = useState<string | null>(null);
  const [kalanDeneme, setKalanDeneme] = useState(KOD_DENEME_HAKKI);
  const [kalanSure, setKalanSure] = useState(KOD_GECERLILIK_SANIYE);
  const [yenidenGonderBekleme, setYenidenGonderBekleme] = useState(YENIDEN_GONDER_BEKLEME_SANIYE);

  /**
   * Saniyede bir çalışan tek sayaç.
   *
   * İki ayrı zamanlayıcı kurulsaydı (kod süresi ve yeniden gönderme) tarayıcı sekmesi
   * arka plandayken ikisi farklı hızlarda gecikir ve ekranda tutarsız iki süre görünürdü.
   */
  useEffect(() => {
    const zamanlayici = window.setInterval(() => {
      setKalanSure((onceki) => (onceki <= 0 ? 0 : onceki - 1));
      setYenidenGonderBekleme((onceki) => (onceki <= 0 ? 0 : onceki - 1));
    }, 1000);
    return () => window.clearInterval(zamanlayici);
  }, []);

  /** Odak kod alanına verilir: kullanıcı SMS'ten geldiğinde ilk işi kodu yazmaktır. */
  const kodAlaniRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    kodAlaniRef.current?.focus();
  }, []);

  const kodGecersiz = kalanDeneme <= 0;

  const dogrula = useCallback(
    async (olay: FormEvent<HTMLFormElement>): Promise<void> => {
      olay.preventDefault();
      if (gonderiliyor || kodGecersiz || kod.length !== KOD_UZUNLUGU) {
        return;
      }

      setHataMesaji(null);
      setGonderiliyor(true);

      try {
        const yanit = await istek<DogrulamaYaniti>({
          yol: '/auth/session/mfa',
          yontem: 'POST',
          govde: {
            challenge_id: challengeId,
            code: kod,
            remember_device: cihaziHatirla,
            device_type: CIHAZ_TURU_WEB,
            device_name: cihazAdi.trim(),
          },
        });

        // Başlıktaki not 6 — sır yalnız burada, bir kez döner.
        if (typeof yanit.device_secret === 'string' && yanit.device_secret !== '') {
          cihazSirriniYaz(yanit.device_secret);
        }

        if (yanit.device_limit_reached === true) {
          // Başlıktaki not 7 — oturum açılır, kullanıcı bilgilendirilir ve listeye
          // doğrudan bağlantı verilir (`15` §15.1).
          notifications.show({
            color: 'uyari',
            title: metin('cihaz.sinirBaslik'),
            message: (
              <>
                {metin('cihaz.sinirMetin')}{' '}
                <Anchor component={Link} to="/cihazlarim" size="sm">
                  {metin('cihaz.listeyeGit')}
                </Anchor>
              </>
            ),
            // Kapanmaz: kullanıcı bağlantıya tıklamadan kaybolsaydı, cihazının neden
            // kaydedilmediğini bir daha hiçbir yerde göremezdi.
            autoClose: false,
          });
        }

        await oturumuBaslat(yanit.session.token, yanit.session.expires_at);
        // `void` — yönlendirici bir `Promise` döndürür; beklenecek bir sonucu yoktur.
        void yonlendir(baslangic.donulecekYol, { replace: true });
      } catch (hata) {
        setKod('');
        if (hata instanceof ApiHatasi && hata.kod === 'AUTH_REQUIRED') {
          // Başlıktaki not 3 — sunucu ayrım yapmadığı için sayaç istemcidedir.
          const kalan = kalanDeneme - 1;
          setKalanDeneme(kalan);
          setHataMesaji(
            kalan <= 0
              ? metin('dogrulama.kodGecersiz')
              : metin('dogrulama.kodHatali', { kalan }),
          );
        } else if (hata instanceof ApiHatasi && hata.kod === 'RATE_LIMITED') {
          setHataMesaji(metin('giris.cokFazlaDeneme'));
        } else {
          setHataMesaji(hata instanceof ApiHatasi ? hata.message : metin('hata.beklenmeyen'));
        }
      } finally {
        setGonderiliyor(false);
      }
    },
    [
      baslangic.donulecekYol,
      challengeId,
      cihazAdi,
      cihaziHatirla,
      gonderiliyor,
      kalanDeneme,
      kod,
      kodGecersiz,
      oturumuBaslat,
      yonlendir,
    ],
  );

  const yenidenGonder = useCallback(async (): Promise<void> => {
    if (yenidenGonderBekleme > 0 || gonderiliyor) {
      return;
    }

    setHataMesaji(null);
    setGonderiliyor(true);

    try {
      const yanit = await istek<YenidenGonderYaniti>({
        yol: '/auth/session/mfa/resend',
        yontem: 'POST',
        govde: { challenge_id: challengeId },
      });

      // Başlıktaki not 8 — kimlik yenilenir, sayaçlar sıfırlanır.
      setChallengeId(yanit.challenge_id);
      if (typeof yanit.phone_masked === 'string' && yanit.phone_masked !== '') {
        setTelefonMaskeli(yanit.phone_masked);
      }
      setKod('');
      setKalanDeneme(KOD_DENEME_HAKKI);
      setKalanSure(KOD_GECERLILIK_SANIYE);
      setYenidenGonderBekleme(YENIDEN_GONDER_BEKLEME_SANIYE);
      notifications.show({ color: 'bilgi', message: metin('dogrulama.yenidenGonderildi') });
      kodAlaniRef.current?.focus();
    } catch (hata) {
      setHataMesaji(yenidenGonderHatasi(hata));
    } finally {
      setGonderiliyor(false);
    }
  }, [challengeId, gonderiliyor, yenidenGonderBekleme]);

  return (
    <KimlikDuzeni
      baslik={metin('dogrulama.baslik')}
      aciklama={
        telefonMaskeli === ''
          ? undefined
          : metin('dogrulama.aciklama', { telefon: telefonMaskeli })
      }
      altBilgi={
        <Anchor component={Link} to="/giris" size="sm">
          {metin('sifirlamaTalebi.girisEkranina')}
        </Anchor>
      }
    >
      <form onSubmit={(olay) => void dogrula(olay)} noValidate>
        <Stack gap="md">
          <HataUyarisi mesaj={hataMesaji} />

          <Input.Wrapper
            label={metin('dogrulama.kod')}
            description={
              kalanSure > 0
                ? metin('dogrulama.kalanSure', { sure: saniyeyiBicimle(kalanSure) })
                : metin('dogrulama.suresiDoldu')
            }
          >
            <Group justify="center" mt="xs">
              <PinInput
                ref={kodAlaniRef}
                length={KOD_UZUNLUGU}
                type="number"
                inputMode="numeric"
                oneTimeCode
                value={kod}
                onChange={setKod}
                disabled={kodGecersiz}
                aria-label={metin('dogrulama.kod')}
              />
            </Group>
          </Input.Wrapper>

          <Checkbox
            label={metin('dogrulama.cihaziHatirla')}
            checked={cihaziHatirla}
            onChange={(olay) => setCihaziHatirla(olay.currentTarget.checked)}
          />

          {/* Cihaz adı yalnız kaydedilecekse sorulur; kaydedilmeyecek bir adı istemek
              kullanıcıyı gereksiz bir karara zorlar. Uyarı metni zorunludur (not 5). */}
          {cihaziHatirla ? (
            <TextInput
              label={metin('dogrulama.cihazAdi')}
              description={metin('dogrulama.cihazAdiUyari')}
              placeholder={metin('dogrulama.cihazAdiYerTutucu')}
              value={cihazAdi}
              onChange={(olay) => setCihazAdi(olay.currentTarget.value)}
              maxLength={60}
            />
          ) : null}

          <Button
            type="submit"
            loading={gonderiliyor}
            disabled={kodGecersiz || kod.length !== KOD_UZUNLUGU}
            fullWidth
          >
            {metin('dogrulama.gonder')}
          </Button>

          <Group justify="center">
            <Button
              variant="subtle"
              size="compact-sm"
              onClick={() => void yenidenGonder()}
              disabled={yenidenGonderBekleme > 0 || gonderiliyor}
            >
              {yenidenGonderBekleme > 0
                ? metin('dogrulama.yenidenGonderBekle', {
                    sure: saniyeyiBicimle(yenidenGonderBekleme),
                  })
                : metin('dogrulama.yenidenGonder')}
            </Button>
          </Group>

          <Text size="xs" c="notr.6" ta="center">
            {metin('giris.tanimaNotu')}
          </Text>
        </Stack>
      </form>
    </KimlikDuzeni>
  );
}

/** `04` AU-03'ün iki hata kodu — `429` ve `404`. */
function yenidenGonderHatasi(hata: unknown): string {
  if (!(hata instanceof ApiHatasi)) {
    return metin('hata.beklenmeyen');
  }
  switch (hata.kod) {
    case 'RATE_LIMITED':
      return metin('dogrulama.cokSik');
    case 'NOT_FOUND':
      return metin('dogrulama.bulunamadi');
    case 'UPSTREAM_UNAVAILABLE':
      return metin('giris.smsGonderilemedi');
    default:
      return hata.message;
  }
}
